import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCookie, FiSettings, FiCheck, FiX, FiShield, FiEye, FiBarChart3, FiHeart } = FiIcons;

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    functional: false,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem('cookie-consent');
    const consentTimestamp = localStorage.getItem('cookie-consent-timestamp');
    
    if (!savedConsent || isConsentExpired(consentTimestamp)) {
      // Show banner after a short delay
      setTimeout(() => {
        setIsVisible(true);
      }, 2000);
    } else {
      // Load saved preferences
      const savedPreferences = JSON.parse(savedConsent);
      setPreferences(savedPreferences);
      applyCookieSettings(savedPreferences);
    }
  }, []);

  const isConsentExpired = (timestamp) => {
    if (!timestamp) return true;
    const consentDate = new Date(timestamp);
    const now = new Date();
    const daysDiff = (now - consentDate) / (1000 * 60 * 60 * 24);
    return daysDiff > 365; // Expire after 1 year
  };

  const applyCookieSettings = (settings) => {
    // Apply analytics cookies
    if (settings.analytics) {
      // Initialize analytics (placeholder - would integrate with real analytics)
      console.log('Analytics cookies enabled');
      // gtag('config', 'GA_MEASUREMENT_ID');
    } else {
      // Disable analytics
      console.log('Analytics cookies disabled');
    }

    // Apply marketing cookies
    if (settings.marketing) {
      console.log('Marketing cookies enabled');
      // Initialize marketing pixels, etc.
    } else {
      console.log('Marketing cookies disabled');
    }

    // Apply functional cookies
    if (settings.functional) {
      console.log('Functional cookies enabled');
      // Enable chat widgets, embedded content, etc.
    } else {
      console.log('Functional cookies disabled');
    }

    // Announce changes for screen readers
    announceToScreenReader(`Cookie preferences updated. Analytics: ${settings.analytics ? 'enabled' : 'disabled'}, Marketing: ${settings.marketing ? 'enabled' : 'disabled'}, Functional: ${settings.functional ? 'enabled' : 'disabled'}.`);
  };

  const announceToScreenReader = (message) => {
    const announcer = document.getElementById('cookie-announcer') || 
                    document.createElement('div');
    
    if (!announcer.id) {
      announcer.id = 'cookie-announcer';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      document.body.appendChild(announcer);
    }

    announcer.textContent = message;
  };

  const saveConsent = (newPreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
    localStorage.setItem('cookie-consent-timestamp', new Date().toISOString());
    applyCookieSettings(newPreferences);
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    setPreferences(onlyNecessary);
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const updatePreference = (category, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const cookieCategories = [
    {
      key: 'necessary',
      title: 'Strictly Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They enable basic features like page navigation, security, and accessibility settings.',
      icon: FiShield,
      required: true,
      examples: ['Authentication tokens', 'Security cookies', 'Accessibility preferences'],
      color: 'text-red-600'
    },
    {
      key: 'functional',
      title: 'Functional Cookies',
      description: 'These cookies enhance your experience by remembering your preferences and enabling additional features like chat support.',
      icon: FiSettings,
      required: false,
      examples: ['Language preferences', 'Chat widgets', 'Embedded content'],
      color: 'text-blue-600'
    },
    {
      key: 'analytics',
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website, allowing us to improve our services.',
      icon: FiBarChart3,
      required: false,
      examples: ['Google Analytics', 'Page view tracking', 'User behavior analytics'],
      color: 'text-green-600'
    },
    {
      key: 'marketing',
      title: 'Marketing Cookies',
      description: 'These cookies are used to deliver relevant advertisements and track the effectiveness of our marketing campaigns.',
      icon: FiHeart,
      required: false,
      examples: ['Social media pixels', 'Advertising networks', 'Retargeting cookies'],
      color: 'text-purple-600'
    }
  ];

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/20 backdrop-blur-sm pointer-events-auto"
          onClick={() => !showDetails && setIsVisible(false)}
        />

        {/* Cookie Banner */}
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.95 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="relative w-full max-w-4xl mx-4 mb-4 bg-white rounded-2xl shadow-2xl border border-gray-200 pointer-events-auto"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <SafeIcon icon={FiCookie} className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 id="cookie-banner-title" className="text-xl font-semibold text-gray-900">
                    Cookie Preferences
                  </h2>
                  <p id="cookie-banner-description" className="text-sm text-gray-600 mt-1">
                    We use cookies to enhance your experience and improve our services
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsVisible(false)}
                className="p-2 text-gray-400 hover:text-gray-600 focus-ring rounded-lg"
                aria-label="Close cookie banner"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {!showDetails ? (
              /* Simple View */
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed">
                  We respect your privacy and are committed to transparency about how we use cookies. 
                  You can choose which types of cookies to accept or customize your preferences.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiEye} className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-blue-800 mb-1">Privacy First</h3>
                      <p className="text-blue-700 text-sm">
                        We follow GDPR guidelines and only use cookies that enhance your experience. 
                        Your data is never sold or shared with third parties without your consent.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary/90 transition-colors focus-ring"
                  >
                    Accept All Cookies
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors focus-ring"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors focus-ring flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiSettings} className="w-4 h-4" />
                    <span>Customize</span>
                  </button>
                </div>

                <div className="text-center">
                  <a 
                    href="https://togethr.space/#/privacy-policy" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline focus-ring rounded"
                  >
                    Read our full Privacy Policy
                  </a>
                </div>
              </div>
            ) : (
              /* Detailed View */
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Customize Your Cookie Preferences
                  </h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-sm text-gray-500 hover:text-gray-700 focus-ring rounded px-2 py-1"
                  >
                    ← Back to simple view
                  </button>
                </div>

                <div className="space-y-4">
                  {cookieCategories.map((category) => (
                    <div key={category.key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className={`p-2 bg-gray-50 rounded-lg ${category.color}`}>
                            <SafeIcon icon={category.icon} className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-gray-900 mb-1">
                                {category.title}
                              </h4>
                              <div className="flex items-center">
                                {category.required ? (
                                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                                    Required
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => updatePreference(category.key, !preferences[category.key])}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-ring ${
                                      preferences[category.key] ? 'bg-primary' : 'bg-gray-200'
                                    }`}
                                    role="switch"
                                    aria-checked={preferences[category.key]}
                                    aria-labelledby={`${category.key}-label`}
                                  >
                                    <span className="sr-only">Toggle {category.title}</span>
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        preferences[category.key] ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              {category.description}
                            </p>
                            <div className="text-xs text-gray-500">
                              <strong>Examples:</strong> {category.examples.join(', ')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Your Current Selection:</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    {cookieCategories.map((category) => (
                      <div key={category.key} className="flex items-center space-x-2">
                        <SafeIcon 
                          icon={preferences[category.key] ? FiCheck : FiX} 
                          className={`w-4 h-4 ${preferences[category.key] ? 'text-green-600' : 'text-red-600'}`}
                        />
                        <span className="text-gray-700">{category.title.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary/90 transition-colors focus-ring"
                  >
                    Save My Preferences
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors focus-ring"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors focus-ring"
                  >
                    Reject All
                  </button>
                </div>

                <div className="text-center space-y-2">
                  <p className="text-xs text-gray-500">
                    You can change these preferences at any time in your account settings or by clearing your browser cookies.
                  </p>
                  <a 
                    href="https://togethr.space/#/privacy-policy" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline focus-ring rounded"
                  >
                    Read our full Privacy Policy
                  </a>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Screen reader announcements */}
        <div id="cookie-announcer" className="sr-only" aria-live="polite" aria-atomic="true" />
      </div>
    </AnimatePresence>
  );
};

export default CookieConsent;